<template>
  <config-base class="opc-zra" @view-details="onViewDetailDialog">
    <div slot="config" class="opc-zra__config" ref="config">
      <div class="opc-zra__desc">
        <p>{{ $t('billing.config.select_number_license') }}</p>
        <zm-tooltip
          class="opc-zra__desc-tooltip"
          aria-label="Learn more about Select the number of licenses"
          :content="$t('billing.zoom_revenue_accelerator.tooltip', [title, $t(basePlanNameKey)])"
          placement="top"
        >
          <i
            class="zm-icon-info-outline main-icon"
            role="button"
            @mouseover="() => $tracking.buyInteraction(trackingPlanName + ' options', 'number of ports - tooltip')"
            @click="() => $tracking.buyInteraction(trackingPlanName + ' options', 'number of ports - tooltip')"
          ></i>
        </zm-tooltip>
      </div>
      <div class="opc-zra__quantity">
        <input-number-with-popover
          class="opc-zra__quantity-selector"
          v-model="selectedNumber"
          size="large"
          controls-position="both"
          :min="minHosts"
          :max="maxHosts"
          @change="updatePlanLicense"
          :decrease-tips="decreaseTips"
          label="Select the number of licenses"
        />
      </div>
      <bought-license :type="planType" />
      <alert :type="licenseAlert.type || 'info'" class="opc-zra__license-alert" v-if="licenseAlert">
        <p
          class="opc-zra__license-alert-tips"
          v-html="licenseAlert.content"
        />
      </alert>
      <config-cycle
        v-model="billingCycle"
        :billing-cycle-list="billingCycleList"
        @switchCycleAction="switchCycleAction"
        :unit="planUnitMap.license"
        :show-cycle-alert="showCycleAlert"
        :trackingPlanName="trackingPlanName"
      />
      <preview-payment class="opc-zra__preview-payment" />
    </div>
    <template #footer v-if="canRmv">
      <zm-button data-testid="checkout-button-243" type="link" ghost @click="removePlanByKey" :aria-label="'remove ' + trackingPlanName + ' plan'">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import mx_plansCommon from '@/plans/mx/plan';
import mx_config from '@/plans/ZOOM_REVENUE_ACCELERATOR/mixin';
import ConfigBase from '@/components/config-base';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { BILLING_CYCLE, DEFAULT_MIN_HOST, trackingPlanTypes } from '@/common/constants';
import { plansof } from '@/utils/chain';
import ConfigCycle from '@/components/service/config-billing-cycle/index';
import { planUnitMap } from '@/common/product-unit';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import { logger } from '@/utils/log';
import AddonMixin from '@/plans/mx/addon';
import { isEmptyVal } from '@/utils';
import { get } from 'lodash';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import { utils, ZOOM_REVENUE_ACCELERATOR_DIALOG } from '@zoom/billing-shared';
import { getCurrency, getCurrencySymbol } from "@/utils/currency";
import tieredPlanMixin from '@/plans/mx/tiered-plan'
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import NavBack from '@/components/nav-back.vue';

const ConfigBaseComp = {
  name: 'zoomRevenueAcceleratorbase',
  extends: ConfigBase,
  mixins: [mx_config],
  data() {
    return {
      showLearnMore: false,
      unit: planUnitMap.license,
      showPlanDetails: true,
    };
  },
};

export default {
  name: 'RevenueAcceleratorBase',
  mixins: [mx_plansCommon, mx_config, AddonMixin, tieredPlanMixin],
  components: {
    PreviewPayment,
    Alert,
    ConfigCycle,
    InputNumberWithPopover,
    ConfigBase: ConfigBaseComp,
    BoughtLicense
  },
  data() {
    return {
      selectedNumber: 1,
      billingCycle: '',
      trackingPlanName: trackingPlanTypes.ZOOM_REVENUE_ACCELERATOR,
      planUnitMap,
      showCycleAlert: true,
      licenseWarningAlertTip: ''
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans', 'getSelectedPlansMap', 'planMapFlat', 'getUserCurrentSubscriptions',
      'getCurrentBasePlanInfo', 'getTypeGroupSelectedPlans', 'basePlanTotalLicenses', 'currentBasePlans'
    ]),
    isModal() {
      return this.$context?.is.modal;
    },
    planList() {
      return plansof(this.planMapFlat).type(this.planType).get();
    },
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
    mx_minHosts() {
      return _.get(this.planList, [0, 'min'], DEFAULT_MIN_HOST);
    },
    minHosts() {
      return this.mx_minHosts;
    },
    maxHosts() {
      const basePlanLicenseCount = this.basePlanTotalLicenses;
      const planMaxCount = _.get(this.planList, [0, 'max'], 9999);
      return _.min(_.compact([basePlanLicenseCount, planMaxCount]));
    },
    decreaseTips() {
      return '';
    },
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = _.get(this.planList, [0, 'billingCycleDependBasePlanTypeList'], []);

      return _.chain(this.getUserCurrentSubscriptions)
        .filter((p) => _.includes(dependCyclePlanList, p.type))
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },
    depCurrentAllAddonPlanList() {
      return _.filter(this.getUserCurrentSubscriptions,
        (p) => _.includes(p.dependBasePlanTypeList, this.planType));
    },
    disabledForBulkLicense() {
      return (cycle) => {
        logger.info("bulk-license", cycle);
        return false;
      }
    },
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },
    basePlanAlreadyInCart() {
      const configPlan = plansof(this.getSelectedPlans).type(this.$context.type).get(0);
      const { dependBasePlanTypeList = [] } = configPlan;
      return plansof(this.getSelectedPlans).filter(basePlan => dependBasePlanTypeList.includes(basePlan.type)).has();
    },
    canRmv() {
      return false;
    },
    showMaxLicenseTips() {
      return this.selectedNumber >= this.maxHosts;
    },
    licenseAlert() {
      if (this.showMaxLicenseTips) {
        return {
          type: 'info',
          content: this.$t('billing.addon_license_limit_tips', [this.title, this.$t(this.basePlanNameKey)])
        }
      } else if (this.licenseWarningAlertTip) {
        return {
          type: 'warning',
          content: this.licenseWarningAlertTip
        }
      } else {
        return null
      }
    },
    basePlanNameKey() {
      return get(this.currentBasePlans, [0, 'parentPlanNameKey']) || get(this.currentBasePlans, [0, 'planNameKey']) || get(this.currentBasePlans, [0, 'typeNameKey'])
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),
    updatePlanLicense(newPorts, oldPorts) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newPorts },
      });
      this.mx_validDownSellLicense(newPorts)

      // event tracking
      this.$tracking.buyInteraction(
        `number of ports`,
        `${this.trackingPlanName} - ${newPorts > oldPorts ? 'increase' : 'decrease'} - ${newPorts}`,
      );
    },
    async switchCycleAction(oldPlan, newPlan) {
      // when the current license less than max, get current license, otherwise get maxHosts
      const quantity = this.selectedNumber <= this.maxHosts ? this.selectedNumber : this.maxHosts;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        newPlanInfo,
      ]);

      this.afterSwitchCycleTracking();
    },
    afterSwitchCycleTracking() {
      // event tracking
      const billingCycle = this.selectedPlan?.billing_cycle;
      billingCycle && this.$tracking.buyInteraction(
        `${this.trackingPlanName} plan`,
        this.$t(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
      );
    },
    async onViewDetailDialog() {
      const { showDialog } = utils;
      const getAmountByBillingCycle = (billingCycle) => {
        const plan = this.billingCycleList.find(({ plan }) => plan.billing_cycle === billingCycle)?.plan
        console.assert(plan && plan.amount, 'can not find amount', billingCycle)
        return plan?.amount
      }
      const propsData = {
        currency: getCurrencySymbol(getCurrency()),
        yearAmountInCents: getAmountByBillingCycle(BILLING_CYCLE.ANNUAL),
        monthAmountInCents: getAmountByBillingCycle(BILLING_CYCLE.MONTHLY),
        visible: true,
        'showClose': !this.isModal,
      };
     let dialogIns= await showDialog(ZOOM_REVENUE_ACCELERATOR_DIALOG, propsData, {}, {
          showIns: true,
          class: { 'opc-modal-dialog': this.isModal },
          scopedSlots: {
            back: () => this.isModal
              ? <NavBack backHandler={() => {
                dialogIns.onDetailDialogClose()
                dialogIns = null;
              }} class="mb24" />
              : null,
          },
        },
      );
    }
  },
  created() {
    const selectedPlan = this.selectedPlan;
    if (!isEmptyVal(selectedPlan)) {
      this.billingCycle = selectedPlan.billing_cycle;
      this.selectedNumber = selectedPlan.cartInfo?.selected_number;
    }

    // refresh page
    this.$nextTick(() => {
      this.mx_validDownSellLicense(this.selectedNumber);
    });
  },
  watch: {
    showMaxLicenseTips: {
      immediate: true,
      handler(val) {
        val && this.$tracking.buyInteraction(
          'banner - product level',
          `${this.trackingPlanName} - the maximum amount is ${this.maxHosts} licenses - impression`
        );
      },
    }
  }
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-zra {
  .opc-zm-base-card__name {
    @include media(mobile) {
      display: block;
      padding-right: 60px;
    }
  }
  &__desc {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    & > p {
      color: rgb(0, 3, 31);
      font-size: 18px;
      font-weight: 700;
      line-height: 27px;
    }
    & > i {
      margin-left: 5px;
    }
  }
  &__quantity {
    display: flex;
    align-items: center;
  }

  &__license-alert {
    &.zm-alert {
      margin-top: 12px;
    }

    &-tips {
      color: #666487;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 150%; /* 21px */

      .opc-zra-max-license-alert {
        color: #0B5CFF;
        font-weight: 600;
        text-transform: capitalize;
      }
    }
  }

  &__license-tooltip {
    margin-left: 4px;
    cursor: pointer;
    color: #0B5CFF;
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
